/**
 * Configurar la tienda online.
 *
 * QUE PREGUNTA CONTESTA ESTA PANTALLA
 * -----------------------------------
 * "Que ve el cliente cuando abre el link, y como lo cambio."
 *
 * Hasta que existio, publicar era entrar a la ficha de cada producto y tildar
 * una casilla, 394 veces; el orden de los rubros y el prendido de cada uno solo
 * se podian tocar por SQL; y la unica forma de que un producto tuviera foto era
 * pegar la URL de una imagen alojada en otro lado.
 *
 * EL LINK ARRIBA, Y DOS PESTAÑAS
 * ------------------------------
 * Arriba, siempre, el link para pegar en el Linktree y lo que falta para que
 * sirva. Abajo, dos preguntas distintas (migracion 0093):
 *
 *  · Home: que ve el cliente al abrir el link, sin buscar nada. Es lo que mas
 *    se toca —las ofertas de la semana, que equipos van a la vidriera— y por
 *    eso es la primera y se arma viendolo (`PestanaHome`).
 *  · Que se publica: que esta en la tienda y se encuentra buscando o por
 *    categoria. Los rubros en orden, y adentro sus productos.
 *
 * Antes era una sola pantalla larga: el link, la lista de rubros, las ofertas
 * y el contacto al fondo, en un orden que no era el del home.
 *
 * LO QUE SE TOCA, SE GUARDA
 * -------------------------
 * No hay un boton de guardar general. Cada casilla, cada tarjeta y cada foto
 * disparan su cambio. Los unicos "Guardar" son los de los dialogos del
 * encabezado y de una oferta, porque son campos de texto que se escriben juntos.
 *
 * ESTA NO ES LA PANTALLA DEL CLIENTE. Esa es `/catalogo`, que se ve sin sesion y
 * sin el panel (ver `RUTAS_SIN_PANEL` en `__root.tsx`).
 */
import { useEffect, useMemo, useState } from "react";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Copy, ExternalLink, Home, ListChecks, Store } from "lucide-react";

import { PageHeader } from "@/components/layout/PageHeader";
import { Pagina } from "@/components/layout/Pagina";
import { DialogEncabezado } from "@/components/publicacion/DialogEncabezado";
import { FilaRubro } from "@/components/publicacion/FilaRubro";
import { PestanaHome } from "@/components/publicacion/PestanaHome";
import {
  useActualizarRubro,
  useProductosDeRubros,
  useReordenarRubros,
  useResumenPublicacion,
} from "@/components/publicacion/consultas";
import {
  avisosDePublicacion,
  FILTRO_VACIO,
  hayFiltro,
  lineaDeEstado,
  rubrosConCoincidencias,
  type AccionAviso,
  type FiltroPublicacion,
} from "@/components/publicacion/logica";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Cargando } from "@/components/ui/cargando";
import { Checkbox } from "@/components/ui/checkbox";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { InputBusqueda } from "@/components/ui/input-busqueda";
import { Segmented } from "@/components/ui/segmented";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { mostrarError, mostrarExito } from "@/lib/errores";

export const Route = createFileRoute("/tienda")({ component: Tienda });

type Pestana = "home" | "publicar";

function Tienda(): JSX.Element {
  const resumen = useResumenPublicacion();
  const actualizarRubro = useActualizarRubro();
  const reordenar = useReordenarRubros();

  const [pestana, setPestana] = useState<Pestana>("home");
  const [filtro, setFiltro] = useState<FiltroPublicacion>(FILTRO_VACIO);
  // `whatsapp` abre el dialogo con el foco en el numero: es a donde lleva el
  // aviso de "falta el WhatsApp".
  const [encabezado, setEncabezado] = useState<"cerrado" | "abierto" | "whatsapp">("cerrado");

  const rubros = resumen.data?.rubros ?? [];
  const avisos = useMemo(() => avisosDePublicacion(resumen.data), [resumen.data]);

  /** Cada aviso lleva a donde se arregla. Es la mitad del valor de mostrarlo. */
  function seguirAviso(accion: AccionAviso): void {
    if (accion === "contacto") {
      setEncabezado("whatsapp");
      return;
    }
    setPestana("publicar");
    if (accion === "sin-foto") setFiltro({ ...FILTRO_VACIO, soloSinFoto: true });
    if (accion === "sin-precio") setFiltro({ ...FILTRO_VACIO, soloSinPrecio: true });
  }

  async function ordenar(nuevos: number[]): Promise<void> {
    try {
      await reordenar.mutateAsync(nuevos);
    } catch (error) {
      mostrarError(error);
    }
  }

  async function cambiarVisibilidad(id: number, publicado: boolean): Promise<void> {
    try {
      await actualizarRubro.mutateAsync({ id, cambios: { publicado } });
    } catch (error) {
      mostrarError(error);
    }
  }

  return (
    <Pagina>
      <PageHeader titulo="Tienda online" icono={Store} />

      {resumen.isLoading ? <Cargando texto="Cargando el catálogo…" /> : null}

      {resumen.data ? (
        <>
          <BloqueLink
            url={resumen.data.url_publica}
            estado={lineaDeEstado(resumen.data)}
            avisos={avisos}
            onSeguir={seguirAviso}
          />

          <Segmented
            etiqueta="Qué configurar"
            apariencia="segmentado"
            tamano="md"
            valor={pestana}
            onChange={setPestana}
            opciones={[
              { valor: "home", etiqueta: "Home", icono: Home },
              { valor: "publicar", etiqueta: "Qué se publica", icono: ListChecks },
            ]}
          />

          {pestana === "home" ? (
            <PestanaHome onIrAPublicar={() => setPestana("publicar")} />
          ) : (
            <BloqueRubros
              filtro={filtro}
              onFiltro={setFiltro}
              rubros={rubros}
              onOrdenar={ordenar}
              onVisibilidad={cambiarVisibilidad}
            />
          )}
        </>
      ) : null}

      <DialogEncabezado
        abierto={encabezado !== "cerrado"}
        enfocarWhatsapp={encabezado === "whatsapp"}
        onCerrar={() => setEncabezado("cerrado")}
      />
    </Pagina>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. El link
// ─────────────────────────────────────────────────────────────────────────────

function BloqueLink({
  url,
  estado,
  avisos,
  onSeguir,
}: {
  url: string;
  estado: string;
  avisos: ReturnType<typeof avisosDePublicacion>;
  onSeguir: (accion: AccionAviso) => void;
}): JSX.Element {
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    if (!copiado) return;
    const t = setTimeout(() => setCopiado(false), 2000);
    return () => clearTimeout(t);
  }, [copiado]);

  async function copiar(): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
      mostrarExito("Link copiado", "Pegalo en el Linktree o mandalo por WhatsApp.");
    } catch {
      // Sin permiso de portapapeles (Safari viejo, http sin localhost) el link
      // igual esta a la vista y se puede seleccionar a mano.
      mostrarError(new Error("No pudimos copiarlo. Seleccioná el link y copialo a mano."));
    }
  }

  return (
    <Card padding="normal" className="space-y-3">
      <TituloSeccion icono={Store} descripcion={estado}>
        El link del catálogo
      </TituloSeccion>

      <div className="flex flex-wrap items-center gap-2">
        {/* En el telefono va en su propia linea: al lado de los dos botones
            quedaban 35 px y se leia «h…». */}
        <code className="min-w-0 flex-1 basis-full truncate rounded-md border border-border bg-muted px-3 py-2 text-sm sm:basis-0">
          {url}
        </code>
        <Button type="button" variant="outline" onClick={() => void copiar()}>
          {copiado ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
          {copiado ? "Copiado" : "Copiar"}
        </Button>
        <Button asChild variant="outline">
          <a href={url} target="_blank" rel="noreferrer">
            <ExternalLink aria-hidden="true" />
            Ver como cliente
          </a>
        </Button>
      </div>

      {avisos.length > 0 ? (
        <div className="space-y-2">
          {avisos.map((a) => (
            <Aviso
              key={a.clave}
              tono={a.tono}
              compacto
              accion={
                a.accion === "ninguna" ? undefined : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onSeguir(a.accion)}
                  >
                    {a.accion === "contacto" ? "Completar" : "Ver cuáles"}
                  </Button>
                )
              }
            >
              {a.texto}
            </Aviso>
          ))}
        </div>
      ) : null}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. Que se publica
// ─────────────────────────────────────────────────────────────────────────────

function BloqueRubros({
  filtro,
  onFiltro,
  rubros,
  onOrdenar,
  onVisibilidad,
}: {
  filtro: FiltroPublicacion;
  onFiltro: (f: FiltroPublicacion) => void;
  rubros: ReturnType<typeof useResumenPublicacion>["data"] extends infer R
    ? R extends { rubros: infer L }
      ? L
      : never
    : never;
  onOrdenar: (ids: number[]) => void;
  onVisibilidad: (id: number, publicado: boolean) => void;
}): JSX.Element {
  const filtrando = hayFiltro(filtro);
  const sensores = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  // El orden recien arrastrado se pinta antes de que conteste el servidor.
  const [arrastrado, setArrastrado] = useState<{ de: typeof rubros; ids: number[] } | null>(null);
  const lista =
    arrastrado && arrastrado.de === rubros
      ? arrastrado.ids
          .map((id) => rubros.find((r) => r.id === id))
          .filter((r): r is (typeof rubros)[number] => Boolean(r))
      : rubros;
  // Con filtro, solo los rubros que tienen algo (`rubrosConCoincidencias`).
  const productosPorRubro = useProductosDeRubros(
    lista.map((r) => r.id),
    filtrando,
  );
  const mostrados = rubrosConCoincidencias(lista, filtro, productosPorRubro);

  function alSoltar(e: DragEndEvent): void {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = lista.map((r) => r.id);
    const nuevos = arrayMove(ids, ids.indexOf(Number(active.id)), ids.indexOf(Number(over.id)));
    setArrastrado({ de: rubros, ids: nuevos });
    onOrdenar(nuevos);
  }

  return (
    <Card padding="none">
      <div className="space-y-3 border-b border-border p-4">
        <TituloSeccion descripcion="Lo publicado se encuentra buscando o por categoría. Qué va al home se elige en la otra pestaña. Arrastrá los rubros para ordenarlos y abrí uno para ver sus productos.">
          Qué se publica
        </TituloSeccion>

        <InputBusqueda
          valor={filtro.texto}
          onValorChange={(texto) => onFiltro({ ...filtro, texto })}
          etiqueta="Buscar un producto"
          placeholder="Buscá un producto por nombre"
        />

        <div className="flex flex-wrap gap-4">
          <Checkbox
            label="Solo sin foto"
            checked={filtro.soloSinFoto}
            onChange={(e) => onFiltro({ ...filtro, soloSinFoto: e.target.checked })}
          />
          <Checkbox
            label="Solo sin precio"
            checked={filtro.soloSinPrecio}
            onChange={(e) => onFiltro({ ...filtro, soloSinPrecio: e.target.checked })}
          />
          {filtrando ? (
            <Button type="button" variant="ghost" size="sm" onClick={() => onFiltro(FILTRO_VACIO)}>
              Limpiar
            </Button>
          ) : null}
        </div>
      </div>

      <DndContext sensors={sensores} collisionDetection={closestCenter} onDragEnd={alSoltar}>
        <SortableContext items={lista.map((r) => r.id)} strategy={verticalListSortingStrategy}>
          {mostrados.map((r) => (
            <FilaRubro
              key={r.id}
              rubro={r}
              filtro={filtro}
              abiertoPorFiltro={filtrando}
              ordenable={!filtrando}
              onVisibilidad={(publicado) => onVisibilidad(r.id, publicado)}
            />
          ))}
        </SortableContext>
      </DndContext>
      {filtrando && lista.length > 0 && mostrados.length === 0 ? (
        <EstadoVacio
          tamano="sm"
          titulo="Nada coincide con el filtro."
          detalle="Tocá Limpiar para volver a ver todos los rubros."
        />
      ) : null}
    </Card>
  );
}
